import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Tiny shared cache: one fetch per URL, every subscriber re-renders, `projects:changed` refetches everything. */
const cache = new Map(); // url -> { data, error, promise, subs:Set, interval, timer }

const keyOf = (path, params) => `${path}${params ? `?${new URLSearchParams(params).toString()}` : ""}`;

function entry(url) {
  if (!cache.has(url)) cache.set(url, { data: undefined, error: null, promise: null, subs: new Set(), timer: null });
  return cache.get(url);
}

function load(url) {
  const e = entry(url);
  if (e.promise) return e.promise;
  e.promise = api
    .get(url)
    .then((r) => { e.data = r.data; e.error = null; })
    .catch((err) => { e.error = err; })
    .finally(() => { e.promise = null; e.subs.forEach((fn) => fn()); });
  return e.promise;
}

export function invalidate(prefix = "") {
  cache.forEach((e, url) => { if (url.startsWith(prefix)) load(url); });
}

export function mutate(url, updater) {
  const e = entry(url);
  e.data = typeof updater === "function" ? updater(e.data) : updater;
  e.subs.forEach((fn) => fn());
}

if (typeof window !== "undefined") window.addEventListener("projects:changed", () => invalidate(""));

export function useResource(path, params, { interval = 60000, enabled = true } = {}) {
  const url = keyOf(path, params);
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const e = entry(url);
    const fn = () => force((n) => n + 1);
    e.subs.add(fn);
    if (e.data === undefined && !e.promise) load(url);
    if (interval && !e.timer) e.timer = setInterval(() => { if (document.visibilityState === "visible") load(url); }, interval);
    return () => {
      e.subs.delete(fn);
      if (!e.subs.size && e.timer) { clearInterval(e.timer); e.timer = null; }
    };
  }, [url, interval, enabled]);
  const e = entry(url);
  const refresh = useCallback(() => load(url), [url]);
  return { data: e.data, error: e.error, loading: e.data === undefined && !e.error, refresh, url };
}
